import { createContext, useContext, useRef, useState } from 'react'
import {
  ContentPlanningContextValue,
  ContentPlanningProviderProps,
  GeneratedContent,
} from './ContentPlanningContextTypes'
import { postLengthToNumber } from 'components/Wizards/ContentPlanning/utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation, useQueryClient } from 'react-query'
import { generateSocialCaptions } from 'components/Wizards/generateSocialCaptions'
import { ourUuid } from 'utils'
import { reportError } from 'utils/error'
import {
  CreateCampaignPayload,
  GenerateCampaignDocsPayload,
  createCampaignRequest,
  generateDocsRequest,
} from 'components/CampaignCreationModal/createRequest'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { AiCampaignCreationError } from 'components/CampaignCreationModal/CampaignCreationModal'
import { ChannelOption } from 'components/Wizards/CampaignWizard/Types'
import { CampaignType } from 'components/CampaignCreationModal/Types'
import { AlmanacFile, ViewType } from 'types'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { DOC_PATH } from 'paths'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { fetchBasicAndByDescription } from 'hooks/useBasicVisualElementsAndVisualElementsByDescription'
import { useGenerateDesignContentSync } from 'hooks/queries/workspaces/useGenerateDesignContentSync'
import { MediaType } from 'components/Wizards/schemas/shared/mediaType'
import { useCreateImageQueryGenerationSync } from 'hooks/queries/imageQueryGenerations/useCreateImageQueryGenerationSync'
import { Photos } from 'unsplash-js/dist/methods/search/types/response'
import { sendToApi } from 'api/client'
import { useRootStore } from 'stores/RootStore'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { scheduledPostsKeys } from 'hooks/queries/scheduledPosts/scheduledPostsKeys'

import { TimeOfDay, CustomScheduleConfig } from '../PostTimes/ScheduleSelector/types'
import { scheduleContent } from 'components/Wizards/utils/postScheduling'
import dayjs, { Dayjs } from 'dayjs'
import { VisualElementsClient } from 'api/clients/VisualElementClient'
import { PostingCadence } from 'components/Wizards/ContentPlanning/ScheduleContent/ScheduleContent'
import { generateSchedulingData } from '../../utils'

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

interface Values {
  topic?: string
  caption_length?: string
  count?: number
  filename?: string
  tone?: Array<Tone>
  language?: string
  project?: AlmanacFile
  include_images?: boolean
  brand_kit_id?: number
  width?: number
  height?: number
  start_time?: Date
  post_frequency?: PostingCadence
  post_time?: TimeOfDay
  is_custom_schedule?: boolean
  custom_schedule_data?: CustomScheduleConfig
}

// ============================================================================
// CONTEXT SETUP
// ============================================================================

const MobileContentPlanningContext = createContext<ContentPlanningContextValue>({
  generatedContent: [],
  setGeneratedContent: () => {},
  updatePostSchedule: () => {},
  updatePostCaption: () => {},
  updatePostImage: () => {},
  updatePostDesign: () => {},
  generateCampaign: () => {},
  generateContent: () => Promise.resolve(),
  generationType: DesignChannel.InstagramPost,
  mediaType: MediaType.DocumentWithImages,
  isGenerating: false,
  selectedPostId: undefined,
  setSelectedPostId: () => {},
})

export const MobileContentPlanningContextProvider = MobileContentPlanningContext.Provider

// ============================================================================
// MAIN PROVIDER COMPONENT
// ============================================================================

export const MobileContentPlanningProvider = ({
  children,
  captionsGenerationType,
  generationType,
  mediaType,
}: ContentPlanningProviderProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const { currentFolder } = useCurrentFolderStore()
  const history = useHistory()
  const onDoc = useRouteMatch(DOC_PATH)
  const { drawersStore } = useRootStore()
  const queryClient = useQueryClient()

  const [generatedContent, setGeneratedContent] = useState<GeneratedContent[]>([])
  const [selectedPostId, setSelectedPostId] = useState<string | undefined>(undefined)
  const [isGenerating, setIsGenerating] = useState(false)

  const generateMutation = useGenerateDesignContentSync({ retry: 3 })
  const imageQueryMutation = useCreateImageQueryGenerationSync()

  const generateCaptionsMutationAbortController = useRef<AbortController | null>(null)
  if (generateCaptionsMutationAbortController.current === null)
    generateCaptionsMutationAbortController.current = new AbortController()
  const generateCaptionsMutation = useMutation(generateSocialCaptions)

  const generateCampaignMutation = useMutation(async (payload: CreateCampaignPayload) => {
    const response = await createCampaignRequest(payload)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiCampaignCreationError(
        'There was a problem generating the campaign',
        { errorCode: 'campaign' },
        {
          responseData: response.data,
          request: payload,
        },
      )
    }
    return response.data
  })

  const generateDocsMutation = useMutation(async (payload: GenerateCampaignDocsPayload) => {
    const response = await generateDocsRequest(payload)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiCampaignCreationError(
        `There was a problem generating the campaign docs: ${response.data}`,
        { errorCode: 'campaign' },
        {
          responseData: response.data,
          request: {
            workspaceId,
            createCampaignResponseDataContentCampaignId: payload.campaign_id,
          },
        },
      )
    }
    return response.data
  })

  // ============================================================================
  // CONTENT GENERATION FUNCTIONS
  // ============================================================================

  const generateCaptions = async (values: Values, attempts: number): Promise<GeneratedContent[]> => {
    const caption_length = postLengthToNumber(values?.caption_length)
    const prompt = {
      ...values,
      caption_count: values.count || 5,
      caption_length,
      generation_type: captionsGenerationType,
    }
    const payload = { prompt, workspaceId }
    const signal = generateCaptionsMutationAbortController.current?.signal

    let captionsContent: GeneratedContent[] = []

    return new Promise((resolve, reject) => {
      generateCaptionsMutation.mutate(
        { payload, signal },
        {
          onSuccess(data) {
            const captions = data.map((c) => ({ uuid: ourUuid(), text: c }))
            captionsContent = [...captions]
            if (attempts > 0) {
              generateCaptions(values, attempts - 1)
                .then(resolve)
                .catch(reject)
            } else {
              resolve(captionsContent)
            }
          },
          onError(error) {
            reportError(error)
            reject(error)
          },
        },
      )
    })
  }

  const generateDesignContent = async (values: Values, content: GeneratedContent[], description: string | null) => {
    const count = values.count || 5
    const templatesResponse = await fetchBasicAndByDescription({
      width: values.width || 1080,
      height: values.height || 1080,
      description: description || values.topic || '',
      basicLayoutItems: count,
      byDescriptionItems: count,
    })
    const templates = templatesResponse.visual_elements
    if (!templates.length) throw new Error('No templates found')

    const contentWithTemplates = content.map((content, index) => ({
      ...content,
      template: templates[index % templates.length],
    }))

    setGeneratedContent(contentWithTemplates)

    const newContent = await Promise.all(
      contentWithTemplates.map(async (content) => {
        if (!content.template) return content

        try {
          const request = await generateMutation.mutateAsync({
            source_visual_element_id: content.template.id,
            topic: values.topic,
            language: values.language,
            tone: values.tone,
            brand_kit_id: values.brand_kit_id,
            apply_brand: true,
          })

          const parsedContent = request?.generated_content ? JSON.parse(request.generated_content) : undefined
          return { ...content, content: parsedContent }
        } catch (error) {
          reportError(error, {
            severity: 'error',
            metaData: { templateId: content.template.id, topic: values.topic },
          })
          return content
        }
      }),
    )

    setGeneratedContent([...newContent])
    return newContent
  }

  const generateImages = async (values: Values, content: GeneratedContent[], description: string | null) => {
    const query = encodeURIComponent(description || values.topic || '')
    const response = await sendToApi<Photos>(`/unsplash/photos?query=${query}&page=${1}&per_page=${values.count || 5}`)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new Error('Failed to generate images')
    }
    const urls = response.data.results.map((photo) => photo.urls.regular)
    const newContent = content.map((content, index) => ({
      ...content,
      selected_image: urls[index % urls.length],
    }))

    setGeneratedContent([...newContent])
    return newContent
  }

  const generateContent = async (values: Values) => {
    if (isGenerating || !values.topic) return

    setIsGenerating(true)
    const count = values.count || 5
    const captionAttempts = count / 5

    try {
      // Step 1: Generate captions
      let content = await generateCaptions(values, captionAttempts - 1)
      setGeneratedContent(content)

      // Step 2: Add images/designs if needed
      if (mediaType !== MediaType.Document) {
        const imageQueryGeneration = await imageQueryMutation.mutateAsync({
          prompt: {
            topic: values.topic,
            target: mediaType === MediaType.Design ? 'templates_library' : 'stock_provider',
          },
        })

        if (mediaType === MediaType.Design) {
          content = await generateDesignContent(values, content, imageQueryGeneration.result)
        } else {
          content = await generateImages(values, content, imageQueryGeneration.result)
        }
      }

      // Step 3: Schedule content
      const startDate = values.start_time ? dayjs(values.start_time) : dayjs().add(1, 'day')
      const daysAndTimes = generateSchedulingData(values)
      const scheduledContent = scheduleContent(content, daysAndTimes, startDate)
      setGeneratedContent(scheduledContent)
    } finally {
      setIsGenerating(false)
    }
  }

  // ============================================================================
  // CAMPAIGN GENERATION FUNCTIONS
  // ============================================================================

  const generateDocs = (campaignId: number, values: Values) => {
    if (!generatedContent.length) return

    const channels: Array<ChannelOption> = generatedContent.map((content) => ({
      channel_type: generationType,
      prompt_library: 'default',
      pregenerated_content: content.text,
      post_at: content.postAt?.toISOString(),
      source_visual_element_id: content.template?.id,
      initial_media_file_id: content.initialMediaFileId,
      topic: values.topic,
      tone: values.tone,
      language: values.language,
      brand_kit_id: values.brand_kit_id,
      content: content.content,
    }))

    generateDocsMutation.mutate(
      {
        workspaceId,
        campaign_id: campaignId,
        campaign_type: CampaignType.Planning,
        channels: channels,
        redirect_to_view: ViewType.Calendar,
        prompt: {
          topic: values.topic,
          tone: values.tone,
          language: values.language,
          include_images: values.include_images,
          brand_kit_id: values.brand_kit_id,
        },
      },
      {
        onError: (error) => {
          reportError(error)
        },
        onSuccess: ({ almanac_file }) => {
          // Invalidate calendar and scheduled posts queries to ensure fresh data
          queryClient.invalidateQueries(calendarKeys.all)
          queryClient.invalidateQueries(scheduledPostsKeys.calendar(workspaceId))
          queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))

          if (almanac_file.url && (almanac_file.url !== currentFolder?.url || onDoc)) history.push(almanac_file.url)
          drawersStore.closeAllDrawers()
        },
      },
    )
  }

  const generateCampaign = async (values: Values) => {
    generateCampaignMutation.mutate(
      {
        workspaceId,
        name: values.filename,
        focus: values.topic,
        tones: values.tone,
        project_key: values.project?.id,
      },
      {
        onError: (error) => {
          reportError(error)
        },
        onSuccess: ({ content_campaign }) => {
          generateDocs(content_campaign.id, values)
        },
      },
    )
  }

  // ============================================================================
  // UPDATE FUNCTIONS
  // ============================================================================

  const updatePostSchedule = (uuid: string, newPostAt: Dayjs) => {
    setGeneratedContent((prevContent) =>
      prevContent.map((content) => (content.uuid === uuid ? { ...content, postAt: newPostAt } : content)),
    )
  }

  const updatePostCaption = (uuid: string, newText: string) => {
    setGeneratedContent((prevContent) =>
      prevContent.map((content) => (content.uuid === uuid ? { ...content, text: newText } : content)),
    )
  }

  const updatePostImage = (uuid: string, newImageUrl: string) => {
    setGeneratedContent((prevContent) =>
      prevContent.map((content) => (content.uuid === uuid ? { ...content, selected_image: newImageUrl } : content)),
    )
  }

  const updatePostDesign = async (uuid: string, templateId: number) => {
    try {
      const { data: template, type } = await VisualElementsClient.getElement(templateId)

      if (type !== 'success') {
        throw new Error('Failed to fetch template details')
      }

      setGeneratedContent((prevContent) =>
        prevContent.map((content) =>
          content.uuid === uuid
            ? { ...content, template, content: undefined, previewUrl: template.preview_url }
            : content,
        ),
      )
    } catch (error) {
      reportError('Failed to update post design', { metaData: { error, templateId, uuid } })
    }
  }

  // ============================================================================
  // CONTEXT VALUE & PROVIDER
  // ============================================================================

  const contextValue: ContentPlanningContextValue = {
    generatedContent,
    setGeneratedContent,
    updatePostSchedule,
    updatePostCaption,
    updatePostImage,
    updatePostDesign,
    generateCampaign,
    generateContent,
    generationType,
    mediaType,
    isGenerating,
    selectedPostId,
    setSelectedPostId,
  }

  return <MobileContentPlanningContextProvider value={contextValue}>{children}</MobileContentPlanningContextProvider>
}

export const useMobileContentPlanningContext = () => {
  return useContext(MobileContentPlanningContext)
}
